import { WizDatasource, WizRoot, WizEventManager, WizIcon, WizTranslationManager, WizPopUp } from '@wizeoo/akm-component-lib';
import { BusinessException, EntityModificationPack, Measure, Vision, VisionFilterKey, VisionKey, VisionStep, VisionStepPerimeter, VisionStepPerimeterKey } from '@wizeoo/akm-message-lib';
import { BaselineEngine, VisionVO, WizConstants, RUN_MODE, INJECTION, EM_ACTION, IVisionKeyProperties, DATA_KIND } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE, VISION_PROPERTY } from '../../model/enums';
import { ViewData } from '../../model/view.data';
import { VisionKeyVO } from '../../vo/vision/visionkey.vo';
import { VisionFilterKeyVO } from '../../vo/vision/visionfilterkey.vo';
import { VisionStepPerimeterVO } from '../../vo/vision/visionstepperimeter.vo';
import { WIZ_COMMAND_STATE, WizMathUtils } from '@wizeoo/akm-core-lib';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { AttachmentService } from '../../command/core/attachment/attachment.service';
import { VisionKeyObj } from '../../vo/vision/tools/visionkey.obj';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { FreeDataVO } from '../../vo/core/freedata.vo';
import { ChronocodeVO } from '../../vo/core/chronocode.vo';
import { VisionEngine as BaseVisionEngine } from '@wizeoo/akm-compute-lib';
import { BusinessUnitVO } from '../../vo/core/business-unit.vo';
import { DomainVO } from '../../vo/core/domain.vo';
import { UserInfoCmd } from '../../command/user/userinfo.cmd';
import { GetVisionCommand } from '../../command/workingdata/get-vision.command';


/**
 * Manager des visions.
 * Contient toutes les visions chargées pour le périmètre de l'utilisateur
 */
export class VisionEngine extends BaseVisionEngine {
  
  constructor(model: Model) {
    super(model);
    BaselineEngine.injectionMap.set(INJECTION.VISION_LABEL, this.getVisionLabel.bind(this));
    BaselineEngine.injectionMap.set(INJECTION.VISION_DESC, this.getVisionDescription.bind(this));
    BaselineEngine.injectionMap.set(INJECTION.VISION_ICON, this.getVisionIconOrDie.bind(this));
  }

  public get model(): Model {
    return this._model as Model;
  }
  
  /**
   * Clone d'une vision pour édition
   * @param vis la vision à cloner
   * @return VisionVO,  Un clone
   */
  public clone(vis: VisionVO): VisionVO {
    const clone = new VisionVO(cloneDeep(vis.source));
    this.setVisionBaseProperties(clone);
    return clone;
  }

  /** création d'une nouvelle vision */
  public buildFake(): VisionVO {
    let vision: Vision = new Vision();
    vision.domain = this.model.domainCode;
    vision.businessUnitCode = this.model.businessUnitCode;
    let result = this.newVisionVO(vision);

    // on va chercher EON comme chronocode
    result.chronoCode = ViewData.defaultChronoCode;

    return result;
  }

  /**
   * pour l'IHM, alimentation des propriétés complémentaires sur la vision
   * @param vision 
   */
  public override newVisionVO(vision: Vision): VisionVO {
    let visionVO: VisionVO = new VisionVO(vision);
    this.setVisionVitalProperties(visionVO);
    this.setVisionBaseProperties(visionVO);
    return visionVO;
  }

  /**
   * alimentation des données complémentaires de base sur la vision, pour gestion IHM
   * données de rendu, pour éventuel refresh de la barre d'info et la barre de menu sur update de la vision en cours
   * @param vision 
   * @return la même, avec ses properties alimentées
   */  
  public setVisionBaseProperties(vision: VisionVO): void {

    vision.visionProperties.set(VISION_PROPERTY.LABEL, this.model.localeEngine.newTranslatableVO(vision, WizConstants.FIELD_LABEL)); // l'objet traductible pour le label
    vision.visionProperties.set(VISION_PROPERTY.DESCRIPTION, this.model.localeEngine.newTranslatableVO(vision, WizConstants.FIELD_DESCRIPTION)); // l'objet traductible pour la description

    // icone du mode de lancement
    let runModeIcon: any = null;
    // tooltip du mode de lancement
    let modeTooltip: string = null;

    switch(vision.runMode) {
      case RUN_MODE.PROD:
        runModeIcon = WizIcon.RUN_MODE_PROD;
        modeTooltip = '$.run.mode.prod.$';
        break;
      case RUN_MODE.QUAL:
        runModeIcon = WizIcon.RUN_MODE_QUAL;
        modeTooltip = '$.run.mode.qual.$';
        break;
      case RUN_MODE.TEST:
        runModeIcon = WizIcon.RUN_MODE_TEST;
        modeTooltip = '$.run.mode.test.$';
        break;
      default: 
        runModeIcon = null;
        modeTooltip = null;
    }
    vision.visionProperties.set(VISION_PROPERTY.RUN_MODE_ICON, runModeIcon);
    vision.visionProperties.set(VISION_PROPERTY.RUN_MODE_TOOLTIP, modeTooltip);


  }

  /**
   * alimentation des données complémentaires de base sur la vision, pour gestion IHM
   * données vitales pour le fonctionnement
   * @param vision 
   * @return la même, avec ses properties alimentées
   */  
  public setVisionVitalProperties(vision: VisionVO): void {  
    vision.visionProperties.set(VISION_PROPERTY.LOADED, false); // la vision est complétement chargée (avec ses collections)
    vision.visionProperties.set(VISION_PROPERTY.ISCONFIGAPP, false); // la vision est complétement chargée (avec ses collections)
    vision.visionProperties.set(VISION_PROPERTY.DOMBU, vision.domain + ' / ' + vision.businessUnitCode); // pour la fallback
    vision.visionProperties.set(VISION_PROPERTY.METRICS, null); // VisionStat pour les métriques
  }

  /** Data source des visions (pour vue vision) : filtré sur la BU */
  public visionDS: WizDatasource<VisionVO> = new WizDatasource<VisionVO>([])
    .setSortFields([{ property: 'code', way: 'asc' }])
    .build();

  /** alimentation des visions du modèle sur find */
  public feedVisions(vos: VisionVO[], silent: boolean = false): void {
    super.feedVisions(vos);
    this.feedBuDS(silent); // mise à jour du pool de BUs
  }

  /** alimentation du pool de vision de la BU active uniquement */
  public feedBuDS( silent: boolean = false): void {
    this.visionDS.setSource(this.getCurrentBuVisions());
    if(!silent) {
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FEED_VISIONS, this.visionDS));  
    }
  }

  /** va retourner une collection de visions de la BU active (Model.businessUnitCode) */
  public getCurrentBuVisions(): VisionVO[] {
    let visions: VisionVO[] = [];
    const modelDic = this.model.visionsDic;
    const buCode = this.model.businessUnitEngine.currentBU?.businessUnitCode;
    // console.log('vision engine', bu, modelDic);
    if (!!this.model.visionsDic && this.model.visionsDic.has(buCode)) {
      visions = Array.from(this.model.visionsDic.get(buCode).values());
    }
    return visions;
  }

  /** va retourner une collection de visions du domaine */
  public getCurrentDomainVisions(): VisionVO[] {
    let visions: VisionVO[] = [];
    const modelArr = this.model.visions;
    const dom = this.model.domainCode;
    // console.log('vision engine', bu, modelDic);
    modelArr.forEach((v: VisionVO) => {
      if(v.domain === dom) {
        visions.push(v);
      }
    });
    return visions;
  }

  /**
   * génération d'un prototype de step
   * @param clone le clone en cours de traitement
   * @param level le niveau désiré
   * @return le step s'il existe déjà, un nouveau step sinon.
   * Et il est ajouté aux visionSteps de la vision
   */
  public addStep(clone: VisionVO, level: number): VisionStep {
    
    let step: VisionStep;
    if (clone.visionSteps && !!clone.visionSteps[level]) { // existe
      step = clone.visionSteps[level];
    } else { // sinon, on crée pas, on retourne l'existant
      step = new VisionStep();
      step.domain = clone.domain;
      step.businessUnitCode = clone.businessUnitCode;
      step.visionCode = clone.code;
      step.chronoCode = clone.chronoCode;
      step.code = 'STEP' + level;
      step.level = level;
      
      if(!clone.visionSteps) {
        clone.visionSteps = {};
      }
      clone.visionSteps[step.level] = step; // ajout à la pile
    }
    return step;
  }

  /**
   * nettoyage des steps avant tentative de persistence
   * @param clone en cours de création
   * @param source l'objet BDD
   */
  public cleanStepsAndMeasures(clone: VisionVO, source: VisionVO): void {
    
    // synchro depuis les collections d'origine (err sur incExc)
    const sourceMap: Map<number, VisionStep> = new Map(Object.values(source.visionSteps || {}).map((item) => [item.level, item]));
    
    // synchronisation des visionSteps
    Object.values(clone.visionSteps || {}).forEach((tobe: VisionStep) => {
      if (sourceMap.has(tobe.level)) {
        const exist = sourceMap.get(tobe.level);
        tobe.id = exist.id;
        tobe.uid = exist.uid;
        tobe.version = exist.version;
      }
    });
    
    // synchronisation des mesures
    if (clone.measures?.length > 0) {
      
      // let finalMeasures: Measure[] = []; // ce qu'on va vraiment garder

      // map d'unicité par code|level
      let finalMeasuresMap: Map<string, Measure> = new Map(); // ce qu'on va vraiment garder

      let sourceMeasureMap: Map<string, Measure[]> = new Map(); // les mesures de la source
      source.measures?.forEach(m => { // les packs de mesures par code de la source
        if(!sourceMeasureMap.has(m.code)) {
          sourceMeasureMap.set(m.code, []);
        }
        sourceMeasureMap.get(m.code).push(m);
        // console.log('source: ', m.shortCode, m.stepLevel, m.id);
      });



      clone.measures.forEach((meas: Measure) => {
        if (sourceMeasureMap.has(meas.code)) { // on prend la ou les vraies mesures  
          sourceMeasureMap.get(meas.code).forEach(m => { 
            let key: string = [m.code, m.stepLevel].join(WizConstants.KEY_SEPARATOR);
            finalMeasuresMap.set(key, m);
          });

          // finalMeasures = finalMeasures.concat(sourceMeasureMap.get(meas.code)); 
        } else { // existe pas encore, on va créer en level0, et on alimente ce qui manque sur le prototype
          meas.stepLevel = 0;
          meas.vid = clone.id;
          meas.vision = clone.code;
          let key: string = [meas.code, meas.stepLevel].join(WizConstants.KEY_SEPARATOR);
          finalMeasuresMap.set(key, meas);
          // finalMeasures.push(meas);
        }
      });

      clone.measures = [...finalMeasuresMap.values()];
    } else {
      clone.source.measures = [];
    }

    if (Object.values(clone.visionFilterKeys || {}).length <= 0) {
      clone.visionFilterKeys = {}; // on enregistre une filterKey Propre
    }

    clone.visionStepPerimeters = source.visionStepPerimeters; // TODO synchroniser quand les perimeters seront dispos
  }

  /**
   * contrôle d'utilisation d'une mesure
   * @param fdCode le code du FD (famille de mesure)
   * @param code le code (FDV) recherché. Si une vision l'utilise, c'est TRUE
   */
  public checkMeasureIsUsed(domain: string, bu: string, fdCode: string, code: string): boolean {
    let result: boolean = false;
    const seeked: string = fdCode + ViewData.CONTEXT_SEPARATOR + code;
    const map: Map<string, Measure> = new Map<string, Measure>();
    this.model.visions.forEach((v: VisionVO) => {
      if (v.domain === domain && v.businessUnitCode === bu && v.measures) {
        v.measures.forEach((m: Measure) => {
          map.set(m.code, m);
        });
      }
    });
    if (map.has(seeked)) {
      // console.log('seeked', fdCode, code);
      result = true;
    }

    return result;
  }

  /**
   * contrôle d'utilisation d'une mesure (pour un FD)
   * @param fdCode le code du FD (famille de mesure)
   * Si une vision l'utilise, c'est TRUE
   */
  public checkMeasureIsUsedForFD(fdCode: string): boolean {
    let result: boolean = false;
    const seeked: string = fdCode;
    const map: Map<string, Measure> = new Map<string, Measure>();
    this.model.visions.forEach((v: VisionVO) => {
      if (v.measures) {
        v.measures.forEach((m: Measure) => {
          map.set(m.code.split(ViewData.CONTEXT_SEPARATOR)[0], m);
        });
      }
    });
    if (map.has(seeked)) {
      result = true;
    }

    return result;
  }

  /** détection d'un changement dans la structure des clés */
  public keyChanged(source: VisionVO, clone: VisionVO): boolean {
    let result: boolean = false;
    if (Object.values(source.visionKeys || {}).length !== Object.values(clone.visionKeys || {}).length) { // plus le même nombre de clés
      return true;
    }

    // on vérifie chaque step
    Object.values(source.visionSteps || {}).forEach((srcStep: VisionStep) => {
      const cloneStep: VisionStep = BaselineEngine.getVisionStepByLevel(clone, srcStep.level);

      let srcStepKeys: VisionKey[] = BaselineEngine.getStepKeys(source, srcStep);
      let cloneStepKeys: VisionKey[] = BaselineEngine.getStepKeys(clone, cloneStep);
      if(srcStepKeys.length !== cloneStepKeys.length) { // plus le même nombre de clés
        result = true;
      } else {
        let i: number = 0;
        
        for (i = 0; i < srcStepKeys.length; i++) {
          if (srcStepKeys[i]?.code !== cloneStepKeys[i]?.code) {
            result = true;
          }
        }
      }

    });

    return result;
  }

  /**
   * retourne le step max pour une vision
   * @param vision 
   * @returns le n° du step le plus bas
   */
  public getVisionMaxLevel(vision: VisionVO): number {
    let result: number = 0;
    result = Object.values(vision.visionSteps || {}).length;
    return result;
  }

  /**
   * retourne le libellé dans la bonne langue pour un step
   * @param visionstep 
   * @returns le libellé
   */
  public getStepLabel(visionstep: VisionStep): string {
    return this.model.localeEngine.translate(visionstep.label, visionstep.code);
  }

  /** accesseur sur le label */
  public getKeyLabel(key: VisionKeyVO | VisionFilterKeyVO): string {
    let result: string = '';
    if (ViewData.VISION_CHRONO_REGEXEP.test(key.code)) {
      const lvl: number = Number(key.code.split(ViewData.CONTEXT_SEPARATOR)[1]); // prise du level
      result = this.model.chronocodeEngine.getChronocodeLevel(key.getRelatedChrono(), lvl).trsLabel; //  this._relatedChronocodeLevel != null ? this._relatedChronocodeLevel.label.entries[this.model.localeEngine.getLocale()].label : this._source.code;
    } else {
      // la key est liée à un freeData
      const lvl: number = Number(key.code.split(ViewData.CONTEXT_SEPARATOR)[1]); // prise du level
      if(!!key.getRelatedFd()) {
        result = this.model.freedataEngine.getFreeDataLevel(key.getRelatedFd(), lvl).trsLabel;
      } else {
        result = 'missing data';
      }
      
    }
    return result;
  }

  /**
   * accesseur sur les propriétés complémentaires des visions, pour l'IHM
   * @param vision cible
   * @param property VISION_PROPERTY, code du champ de la propriété
   * @param value valeur cible pour la prop
   */
  public setVisionProperties(vision: VisionVO, property: VISION_PROPERTY, value: any): void {
    vision.visionProperties.set(property, value);
  }

  /**
   * accesseur sur les propriétés complémentaires des visions, pour l'IHM
   * @param vision cible
   * @param property code du champ de la propriété
   */
  public getVisionProperties(vision: VisionVO, property: string): any {
    return vision?.visionProperties.get(property) || null;
  }

  /**
   * libellé du step
   * 
   * @param vision 
   * @param visionstep 
   * @param withLevel false par défaut. Si true, on construit aussi le n° de level
   * @returns 
   */
  public getVisionStepLabel(vision: VisionVO, visionstep: VisionStep, withLevel: boolean = false): string {
    
    let result: string = this.model.localeEngine.translate(visionstep.label, visionstep.code);
    if (withLevel) {
      result =  visionstep.level + ' - ' + result;
    }
    return result;
  }


  /**
   * description de la vision
   * @param vision 
   * @returns 
   */
  public getVisionDescription(vision: VisionVO): string {
    if (vision.visionProperties.get(VISION_PROPERTY.ISCONFIGAPP) == true) {
      return WizTranslationManager.getInstance().translateKey('$.config.app.desc.$');
    }
    return this.model.localeEngine.translate(vision.description);
  }

  /** 
   * alimentation de la collection de stepPerimeters par bu / vision 
   * @param vision la vision cible
   * @param vos ses vsps
   */
  public feedVsps(vision: VisionVO, vos: VisionStepPerimeterVO[]): void {
    
    if (!this.model.visionStepPerimetersDic) { // création du dico
      this.model.visionStepPerimetersDic = new Map<string, Map<string, Map<string, VisionStepPerimeter>>>();
    }
    if (!this.model.visionStepPerimetersDic.has(vision.businessUnitCode)) { // création du dico par BU
      this.model.visionStepPerimetersDic.set(vision.businessUnitCode, new Map<string, Map<string, VisionStepPerimeter>>());
    }
    if (!this.model.visionStepPerimetersDic.get(vision.businessUnitCode).has(vision.code)) { // création du dico par BU par vision
      this.model.visionStepPerimetersDic.get(vision.businessUnitCode).set(vision.code, new Map<string, VisionStepPerimeter>());
    }

    vos.forEach((vsp: VisionStepPerimeterVO) => {
      this.model.visionStepPerimetersDic.get(vsp.businessUnitCode).get(vsp.visionCode).set(vsp.code, vsp.source);
    });
  }


  /** retour la collection de vsp pour une vision (tous steps) */
  public getStepPerimeterList(vision: VisionVO): Map<string, VisionStepPerimeter> {
    let result: Map<string, VisionStepPerimeter> = null;
    if(     this.model.visionStepPerimetersDic 
            && this.model.visionStepPerimetersDic.has(vision.businessUnitCode) 
            && this.model.visionStepPerimetersDic.get(vision.businessUnitCode).has(vision.code)){
      result = this.model.visionStepPerimetersDic.get(vision.businessUnitCode).get(vision.code);
    }
    return result;
  }

  /** retour la collection de vsp pour une vision (tous steps) */
  public getStepPerimetersByLevel(vision: VisionVO, level: number): VisionStepPerimeter[] {
    let result: VisionStepPerimeter[] = [];
    if (!!this.getStepPerimeterList(vision)) {
      Array.from(this.getStepPerimeterList(vision).values()).forEach((vsp: VisionStepPerimeter) => {
        if (vsp.visionStepLevel === level) {
          result.push(vsp);
        }
      });
    }
    return result;
  }

  /** 
   * retour l'instance BDD d'un vsp spécifique 
   * 
   * @param clone du vsp
   * @returns le vrai
   */
  public getStepPerimeter(clone: VisionStepPerimeterVO): VisionStepPerimeterVO {
    let result: VisionStepPerimeterVO = null;
    // TODO TODO 
    let vsps: Map<string, VisionStepPerimeter> = this.getStepPerimeterList(clone.visionVO);
    
    if(!!vsps) {
      let vsp: VisionStepPerimeter = vsps.get(clone.code);
      if(vsp) {
        result = new VisionStepPerimeterVO(vsp);
      }
    }
    return result;
  }

  public addVspKey(key: string, vsp: VisionStepPerimeterVO): void {
    if(!vsp.keyMap.has(key)) {
      vsp.keyMap.set(key, this.generateNewVspKey(key, vsp) );
    }
  }

  public deleteVspKey(key: string, vsp: VisionStepPerimeterVO): void {
    vsp.keyMap.delete(key);
  }


  public generateNewVspKey(key: string, vsp: VisionStepPerimeterVO): VisionStepPerimeterKey {
    let vspKey: VisionStepPerimeterKey = new VisionStepPerimeterKey();
    vspKey.uid = WizMathUtils.createUUID();
    vspKey.domain = vsp.domain;
    vspKey.businessUnitCode = vsp.businessUnitCode;
    vspKey.visionCode = vsp.visionCode;
    vspKey.perimeterCode = vsp.code;
    vspKey.keyKind = 'INC';
    vspKey.key = key;
    vspKey.version = 0;
    return vspKey;
  }

  /**
   * libellé de la vision
   * @param vision 
   * @returns 
   */
  public getVisionLabel(vision: VisionVO): string {
    let result: string = '';
    if (vision.visionProperties.get(VISION_PROPERTY.ISCONFIGAPP) === true) {
      result =  WizTranslationManager.getInstance().translateKey('$.config.app.title.$');
    } else {
      result =  this.model.localeEngine.translate(vision.label, vision.code);
    }
    return result;
  }

    /**
   * icone de la vision
   * @param vision 
   * @returns 
   */
    public getVisionIconOrDie(vision: VisionVO): Observable<string> {
    // observable sur l'icone
    let iconOrDie: Observable<string> = null;

    if(vision.code === ViewData.wildcardDomain) { // vue de configuration
      iconOrDie = AttachmentService.getInstance().iconOrDie(ViewData.settingsIcon, ViewData.wildcardDomainLiteral, ViewData.wildcardDomainLiteral, ViewData.wildcardMainVision);
    } else if (vision.icon) {
      iconOrDie = AttachmentService.getInstance().iconOrDie(vision.icon, vision.domain, vision.businessUnitCode, ViewData.wildcardMainVision);
    } else {
      iconOrDie = of(null);
    }

    return iconOrDie;
  }   

  /**
   * conversion des obj en visionKey
   * @param clone : la vision en cours de modif
   * @param source : la vision de base, pour reprise des IDs
   * @param obj tableau de capsules Obj
   * @returns {[key: number]: VisionKey} pour alimenter le clone
   */
  public visionObjToVisionKey(clone: VisionVO, source: VisionVO, coll: VisionKeyObj[]): {[key: number]: VisionKey} {
    const result: {[key: number]: VisionKey} = {};
    
    let sourceMap: Map<string, VisionFilterKey> = new Map(Object.values(source.visionKeys || {}).map(item => [item.code, item]));
    let visionKey: VisionKey;

    coll.forEach((obj: VisionKeyObj) => {
      visionKey = new VisionKey();
      visionKey.domain = clone.domain;
      visionKey.businessUnitCode = clone.businessUnitCode;
      visionKey.code = obj.code;
      visionKey.visionCode = clone.code;
      visionKey.chronoCode = clone.chronoCode;
      visionKey.level = obj.level;
      visionKey.levelCode = null; // TODO ou pas ?
      visionKey.order = obj.order;
      visionKey.stepLevel = obj.stepLevel; // position dans le step
      // visionKey.id = null;
      // visionKey.version = 0;
      // visionKey.revisionNumber = null;

      // recherche de la visionKey dans la source pour reprise de l'ID
      if (sourceMap.has(obj.code)) {
        visionKey.id = sourceMap.get(obj.code).id;
        visionKey.version = sourceMap.get(obj.code).version;
      }
      result[visionKey.order]= visionKey;
    });

    return result;
  }

  /**
   * conversion des obj en visionFilterKey
   * @param clone : la vision en cours de modif
   * @param source : la vision de base, pour reprise des IDs
   * @param obj tableau de capsules Obj
   * @returns {[key: string]: VisionFilterKey} pour alimenter le clone
   */
  public visionObjToVisionFilterKey( clone: VisionVO, source: VisionVO, coll: VisionKeyObj[]): {[key: string]: VisionFilterKey} {
    const result: {[key: string]: VisionFilterKey} = {};
    
    let sourceMap: Map<string, VisionFilterKey> = new Map(Object.values(source.visionFilterKeys || {}).map(item => [item.code, item]));

    let visionKey: VisionFilterKey;
    coll.forEach((obj: VisionKeyObj) => {
      visionKey = new VisionFilterKey();
      visionKey.domain = clone.domain;
      visionKey.businessUnitCode = clone.businessUnitCode;
      visionKey.code = obj.code;
      visionKey.visionCode = clone.code;
      visionKey.chronoCode = clone.chronoCode;
      visionKey.level = obj.level;
      visionKey.levelCode = null; // TODO ou pas ?
      visionKey.order = obj.order;
      // visionKey.id = null;
      // visionKey.version = 0;
      // visionKey.revisionNumber = null;

      // recherche de la visionKey dans la source pour reprise de l'ID
      if (sourceMap.has(obj.code)) {
        visionKey.id = sourceMap.get(obj.code).id;
        visionKey.version = sourceMap.get(obj.code).version;
      }
      result[visionKey.order]= visionKey;
    });

    return result;
  }

  /**
   * create/ update d'une vision
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: VisionVO, from: VisionVO, callback: Function): void {
    // suppression des steps vides du clone
    this.cleanStepsAndMeasures(to, from);

    let pack: EntityModificationPack;
    if (to.id > 0) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to); // retour en vue
    }
  }


  /**
   * delete d'une vision
   * @param to le clone à supprimer
   * @param from la vision à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: VisionVO, from: VisionVO, callback: Function): void {
    this.cleanStepsAndMeasures(to, from);
    const pack: EntityModificationPack = EMEngine.removeEM(from);
    new CommitCmd(this.model).commit(pack, this.backFromCall.bind(this), [from], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      // WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: VisionVO = cmd.sourceVO[0] as VisionVO;
      // une seule modification à la fois, packEntry donne l'objet
      const vision: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = vision; // le clone
            this.getVision(vo.code).source = vision;
            this.setVisionBaseProperties(vo); // refresh des propriétés de rendu
            vo.reset();
            // c'est une autre instance
            if(vo.code === this.model.currentVision.code) {
              this.setVisionBaseProperties(this.model.currentVision); // refresh des propriétés de rendu
              this.model.currentVision.reset();
            }
            // this.model.currentVision.source = vision; // maj du vrai
            break;
          case EM_ACTION.CREATE:
            vo.source = vision;
            vo.reset();
            this.visionDS.addItem(vo); // rebuild de la datasource pour ajout de l'item
            this.model.visions.push(vo);
            // première vision de la BU, création de l'entrée pour la BU
            if (!this.getBuVisionMap()) {
              this.model.visionsDic.set(vo.businessUnitCode, new Map<string, VisionVO>());
            }
            this.getBuVisionMap().set(vo.code, vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = vision;
            const toDie: VisionVO = this.getVision(vo.code);
            // suppression des éventuels scenarii
            this.model.scenarioEngine.removeForVision(toDie);

            this.model.visions.splice(this.model.visions.indexOf(toDie), 1);
            this.visionDS.removeItem(toDie); // rebuild de la datasource pour suppression de l'item
            this.getBuVisionMap().delete(vo.code);

            if (this.model.visionCode === toDie.code) {
              this.selectVision(null);
            }

            break;
        }

        WizEventManager.dispatchEvent( WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.ALTER_VISION, [vo, operation]) );
      }
      cmd.viewCallBack(vo, packStatus ); // retour en vue
    }
  }

  public resetVision(): void {
    this.model.visionCode = null;
  }

  public selectVision(value: string) {
    let reallyReset: boolean = false;
    if (value !== this.model.visionCode) {
      reallyReset = true;
      this.model.widgetEngine.resetUserGlobalFilter(); // reset des filtres users
    }
    this.model.visionCode = value;
    // console.log('model', this._visionCode, this.businessUnitCode);
    if (reallyReset) {
      let v: VisionVO = this.model.visionEngine.getVision(value);
      if(!!v) {
        new GetVisionCommand(this.model).get(v.source, this.backFromVisionCall.bind(this)).execute();
      }
    }
  }

  private backFromVisionCall(cmd: GetVisionCommand): void {
   
      if (WizRoot.root()) {
        // on oublie les FEI et les dataLoad. Pas besoin de perdre la BU
        this.model.dataEngine.resetDatas();
        
        new UserInfoCmd(this.model).setUserTimeStamp().execute(); // maj des infos de ts sur le userApp de la vision
        Model.getInstance().navigationEngine.updateUrlContext(true); // mise à jour de l'URL
        Model.getInstance().tabEngine.refreshTabs(false); // Refresh des tabs disponibles pour la vision sélectionnée
        // le 1er event arrive avant construction de la vue
        WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.SELECT_VISION, this.model.currentVision)); // et fire d'un event pour les dépendants de la vision
      }
  }


  /* ################################################
                     gestion perimeters
    ################################################ */


  /**
   * Clone d'un vsp
   * @param vsp à cloner
   * @return VisionVO,  Un clone
   */
  public cloneVsp(vis: VisionStepPerimeterVO): VisionStepPerimeterVO {
    const clone = new VisionStepPerimeterVO(cloneDeep(vis.source));
    return clone;
  }

  /**
   * création d'un nouveau vsp
   * @param vision:VisionVO parente
   * @returns le fake
   */
  public buildFakeVsp(vision:VisionVO): VisionStepPerimeterVO {
    const result: VisionStepPerimeterVO = new VisionStepPerimeterVO();
    result.domain = vision.domain;
    result.businessUnitCode = vision.businessUnitCode;
    result.visionCode = vision.code;
    result.chronoCode = vision.chronoCode;
    result.vid = vision.id; // prise de l'ID de vision
    return result;
  }

  /**
   * delete d'un vsp
   * @param to le clone à supprimer
   * @param from le vsp à supprimer
   * @param callback retour dans la vue
   */
  public removeVsp(to: VisionStepPerimeterVO, from: VisionStepPerimeterVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(from);
    new CommitCmd(this.model).commit(pack, this.backFromCallVsp.bind(this), [from], callback).execute();
  }

 /**
   * create/ update d'un VisionStepPerimeterVO
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public mergeVsp(to: VisionStepPerimeterVO, from: VisionStepPerimeterVO, callback: Function): void {

    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCallVsp.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to); // retour en vue
    }
  }
  
  /**
   * gestion unifiée des retours de service CUD vsp
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCallVsp(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: VisionStepPerimeterVO = cmd.sourceVO[0] as VisionStepPerimeterVO;
      // une seule modification à la fois, packEntry donne l'objet
      const vsp: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = vsp; // le clone
            vo.keyMap = null; // on clear les perimeterKeys
            this.getStepPerimeter(vo).source = vsp; // maj du vrai
            this.getStepPerimeter(vo).keyMap = null;
            break;
          case EM_ACTION.CREATE:
            vo.source = vsp;
            vo.keyMap = null; // refresh de la collection de keys
            this.feedVsps(vo.visionVO, [vo]);
            break;
          case EM_ACTION.DELETE:
            vo.source = vsp;
            const toDie: VisionStepPerimeter = this.deleteStepPerimeter(vo);
            break;
        }
      }
      cmd.viewCallBack(vo); // retour en vue
    }
  }


  /** 
   * supprime un vsp de la map
   * @param clone du vsp
   * @returns le même si la suppression est ok
   */
     public deleteStepPerimeter(vspvo: VisionStepPerimeterVO): VisionStepPerimeter {
      let vsps: Map<string, VisionStepPerimeter> = this.getStepPerimeterList(vspvo.visionVO);
      if(vsps.has(vspvo.code)) {
        let vsp: VisionStepPerimeter = vsps.get(vspvo.code);
        vsps.delete(vspvo.code);
        return vsp;
      }
      
      return null;
    }

 

    /**
     * construction des propriétés complémentaires pour les keys et les filterKeys
     * portées par IVision
     * @param vision IVision
     * @param key: VisionKey | VisionFilterKey
     * @returns IVisionKeyProperties
     */
    public getVisionKeyProperties(vision: VisionVO, key: VisionKey | VisionFilterKey): IVisionKeyProperties {
      if(!vision.keyProperties) {
        vision.keyProperties = new Map();
      }
  
      if(!vision.keyProperties.has(key.code)) {
        
        let parts: string[] = key.code.split(WizConstants.CONTEXT_SEPARATOR);
        
        let code: string = parts[0]; // la clé découpée par # sur code#level
        let level: number = Number(parts[1]); // la clé découpée par # sur code#level
        let label: string;
        let isChronocode: boolean = key.code.startsWith(WizConstants.CHRONO);
        let isFreedata: boolean = !isChronocode;
        let datakind: string;
        let defaultValue: string = null; // la valeur par défaut, issue du Freedata (attention, c'est potentiellement une formule)
        let triggers: Set<string> = new Set();


        if(isFreedata) {
          let fd: FreeDataVO = this.model.freedataEngine.getFreeData(code);
          if(!!fd) { // devrait jamais arriver, suppression d'un FD de la vision
            label = this.model.freedataEngine.getFreeDataLevel(fd, level).trsLabel;
            datakind = fd.dataType;
            
            defaultValue = fd.defaultValue;
            if(fd.defaultTrigger) {
                triggers = new Set(fd.defaultTrigger.split(ViewData.KEY_SEPARATOR));
            }
          }
        } else {
          let chrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(key.chronoCode);
          label = this.model.chronocodeEngine.getChronocodeLevel(chrono, level).trsLabel;
          datakind = DATA_KIND.DATE;
        }
        
        vision.keyProperties.set(key.code, { code, level, label, isChronocode, isFreedata, datakind, defaultValue, triggers });  
      } 
      return vision.keyProperties.get(key.code);
    }


  /**
   * retourne le libellé pour une visionKey ou une filterKey
   * @param vision cible
   * @param key VisionKey | VisionFilterKey
   * @returns 
   */
  public getVisionKeyLabel(vision: VisionVO, key: VisionKey | VisionFilterKey): string {
    return this.getVisionKeyProperties(vision, key).label;
  } 

  /**
   * les visions "fake", qui sont en fait les BUs
   * @param vision 
   * @returns 
   */
  public isConfigApp(vision: VisionVO): boolean {
    return vision.code === ViewData.wildcardDomain;
  }

  /** accesseur sur la BU */
  public getBusinessUnitVO(vision: VisionVO): BusinessUnitVO {
    return this.model.businessUnitEngine.getByBusinessUnitCode(vision.domain, vision.businessUnitCode);
  }

  /** accesseur sur la BU */
  public getDomainVO(vision: VisionVO): DomainVO {
    return this.model.businessUnitEngine.getDomainByCode(vision.domain);
  }


  /** retourne le code domaine et le code BU */
  public getBusinessUnitName(vision: VisionVO): string {
    return this.getBusinessUnitVO(vision)?.name || null;
  }

  /** retourne le code domaine et le code BU */
  public getDomainAndBUStr(vision: VisionVO): string {
    let bu: BusinessUnitVO = this.getBusinessUnitVO(vision);
    if (bu) {
      return vision.domain + ' / '  + bu.name;
    } else {
      return vision.domain + ' / ' + vision.businessUnitCode;
    }
  }

  /** clé de filtre unifiée domaine / Bu */
  public getVisionFilterKey(vision: VisionVO): string {
    return [vision.code, vision.domain , vision.businessUnitCode,  vision.trsLabel,  vision.trsDescription].join(WizConstants.KEY_SEPARATOR);
  }


}
